import { FormControl, FormLabel, Stack, Switch } from '@chakra-ui/react';
import { SIDE_PANEL_COLOR_PICKER_HEIGHT } from 'constants/editor';
import { WIDGET_DEFAULT_FONT } from 'constants/fonts';
import { ColorPicker } from 'modules/Editor/components/ColorPicker';
import {
  Axis,
  ChartSettingsEntry,
  NumberSetting,
  TextSetting,
} from 'modules/Editor/components/SideMenuSettings/ChartSettings/ChartOptions';
import { SideSettingDivider } from 'modules/Editor/components/SideMenuSettings/SidemenuSettingsDivider';
import { FontFamilySelect } from 'modules/common/components/FontFamilySelect';
import { DropdownFontSizeSelect } from 'modules/common/components/FontSizeSelect';
import { SliderNumberInput } from 'modules/common/components/Input/SliderNumberInput';
import { ChangeEvent, memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Color } from 'types/basic.types';
import { WidgetType } from 'types/widget.types';
import { ChartWidgetData } from 'widgets/ChartWidgets/ChartWidget.types';
import { useSideMenuSetting, useWidget } from 'widgets/sdk';

import { TextSettings } from '../TextStyleSettings';
import { getAxisPropertyName } from './ChartSettingsAxis.helpers';
import {
  AxisCommonProps,
  AxisPropertyName,
  MaxValuePropertyName,
  MinValuePropertyName,
  RangeValuePropertyName,
} from './ChartSettingsAxis.types';

interface AxisSettingProps {
  widgetType: WidgetType;
  showDualRangeConfiguration?: boolean;
}

export const AxisSetting = ({ showDualRangeConfiguration = false, widgetType }: Readonly<AxisSettingProps>) => {
  return (
    <Axis>
      <AxisTitle widgetType={widgetType} axisName='xAxis' />
      <AxisTitle widgetType={widgetType} axisName='yAxis' />
      <AxisFont />
      <AxisTitleTextStyle />
      <SideSettingDivider />
      {showDualRangeConfiguration ? <DualMinMaxSection /> : <MinMaxSection />}
      <SideSettingDivider />
      <AxisLine widgetType={widgetType} axisName='xAxis' />
      <AxisLine widgetType={widgetType} axisName='yAxis' />
      <AxisLineSubSettings />
    </Axis>
  );
};

export const AxisTitle = memo(({ widgetType, axisName }: Readonly<AxisCommonProps>) => {
  const { xAxis, yAxis, y2Axis, updateWidget } = useWidget<ChartWidgetData>();
  const { t } = useTranslation('chart_widget', { useSuspense: false });

  const axisPropertyName = getAxisPropertyName(axisName, widgetType);
  const isXYSwapped = axisPropertyName !== axisName;

  const { currentAxisTitle = '', label } = useMemo(() => {
    if (axisName === 'xAxis') {
      return {
        currentAxisTitle: isXYSwapped ? yAxis.title.text : xAxis.title.text,
        label: t('axis.titleLabel.xAxisTitle'),
      };
    }

    if (axisName === 'yAxis') {
      const hasY2Axis = y2Axis != null;
      return {
        currentAxisTitle: isXYSwapped ? xAxis.title.text : yAxis.title.text,
        label: t(hasY2Axis ? 'axis.titleLabel.y1AxisTitle' : 'axis.titleLabel.yAxisTitle'),
      };
    }

    // y2Axis
    return {
      currentAxisTitle: y2Axis?.title.text,
      label: t('axis.titleLabel.y2AxisTitle'),
    };
  }, [axisName, t, xAxis, yAxis, y2Axis, isXYSwapped]);

  const onAxisTitleChange = (event: ChangeEvent<HTMLInputElement>) => {
    updateWidget({ [axisPropertyName]: { title: { text: event.target.value } } });
  };

  return (
    <TextSetting
      label={label}
      value={currentAxisTitle}
      onChange={onAxisTitleChange}
      placeholder={t('axis.titleLabel.placeHolder')}
      data-testid={`${axisPropertyName}-title-input`}
      width={52}
      height={7}
    />
  );
});
AxisTitle.displayName = 'AxisTitle';

export const AxisFont = () => {
  const { t } = useTranslation('chart_widget', { useSuspense: false });
  const label = t('font.label');

  return (
    <ChartSettingsEntry label={label}>
      <Stack direction={'row'}>
        <AxisFontFamily />
        <AxisFontSize />
      </Stack>
    </ChartSettingsEntry>
  );
};

export const AxisFontFamily = () => {
  const {
    xAxis: { title: { style: { fontFamily: titleFontFamily = WIDGET_DEFAULT_FONT } = {} } = {} },
    updateWidget,
  } = useWidget<ChartWidgetData>();

  const onTitleFontFamilyChange = (fontFamily: string) => {
    updateWidget({
      xAxis: { title: { style: { fontFamily } } },
      yAxis: { title: { style: { fontFamily } } },
    });
  };

  return (
    <FontFamilySelect
      contentProps={{ height: SIDE_PANEL_COLOR_PICKER_HEIGHT }}
      fontFamily={titleFontFamily}
      onChange={onTitleFontFamilyChange}
      width='32'
      borderRadius='base'
      buttonProps={{
        size: 'xs',
        fontSize: 'xs',
        height: 7,
      }}
      listenScrollEvent={true}
    />
  );
};

export const AxisFontSize = () => {
  const { t } = useTranslation('chart_widget', { useSuspense: false });
  const {
    xAxis: { title: { style: { fontSize: titleFontSize = '12px' } = {} } = {} },
    updateWidget,
  } = useWidget<ChartWidgetData>();

  const onTitleFontSizeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    updateWidget({
      xAxis: { title: { style: { fontSize: event.target.value + 'px' } } },
      yAxis: { title: { style: { fontSize: event.target.value + 'px' } } },
    });
  };

  return (
    <DropdownFontSizeSelect
      data-testid={'axestitle-fontsize-select'}
      aria-label={t('font.axisTitleFontSizeAriaLabel')}
      value={parseInt(titleFontSize)}
      onChange={onTitleFontSizeChange}
      borderRadius='base'
      size='xs'
      width='4.5rem'
      height='7'
    />
  );
};

export const AxisTitleTextStyle = () => {
  const {
    xAxis: {
      title: {
        style: { color, fontStyle, fontWeight, fontFamily },
      },
    },
    updateWidget,
  } = useWidget<ChartWidgetData>();

  const onFontColorChange = useCallback(
    (color: Color) => {
      updateWidget({
        xAxis: { title: { style: { color } } },
        yAxis: { title: { style: { color } } },
      });
    },
    [updateWidget],
  );

  const onFontWeightChange = useCallback(
    (fontWeight: string) => {
      updateWidget({
        xAxis: { title: { style: { fontWeight } } },
        yAxis: { title: { style: { fontWeight } } },
      });
    },
    [updateWidget],
  );

  const onFontStyleChange = useCallback(
    (fontStyle: string) => {
      updateWidget({
        xAxis: { title: { style: { fontStyle } } },
        yAxis: { title: { style: { fontStyle } } },
      });
    },
    [updateWidget],
  );

  return (
    <TextSettings
      fontFamily={fontFamily}
      fontColor={color}
      fontWeight={fontWeight}
      fontStyle={fontStyle}
      onFontColorChange={onFontColorChange}
      onFontWeightChange={onFontWeightChange}
      onFontStyleChange={onFontStyleChange}
    />
  );
};

interface AxisRangeValueProps {
  'aria-label'?: string;
  propertyName: RangeValuePropertyName;
}

export const AxisRangeValue = memo(({ 'aria-label': ariaLabelProp, propertyName }: AxisRangeValueProps) => {
  const { t } = useTranslation('chart_widget', { useSuspense: false });
  const { seriesMeta, updateWidget } = useWidget<ChartWidgetData>();

  const ariaLabel = t(`axis.range.${propertyName}Label`);
  const label = t(`axis.range.${propertyName.startsWith('min') ? 'minLabel' : 'maxLabel'}`);
  const placeHolderLabel = t('axis.range.placeholder');

  const onValueChange = (Value: string) => {
    updateWidget({ seriesMeta: { [propertyName]: Value } });
  };

  return (
    <NumberSetting
      aria-label={ariaLabelProp ?? ariaLabel}
      label={label ?? propertyName}
      value={seriesMeta[propertyName] ?? ''}
      onChange={onValueChange}
      placeholder={placeHolderLabel}
      containerProps={{
        flexDirection: 'column',
        alignItems: 'start',
        gap: 1,
      }}
      inputProps={{
        height: 7,
      }}
      width={'100%'}
    />
  );
});
AxisRangeValue.displayName = 'AxisRangeValue';

export const MinMaxSection = () => {
  return (
    <Stack direction={'row'} spacing={4}>
      <AxisRangeValue propertyName='minValue' />
      <AxisRangeValue propertyName='maxValue' />
    </Stack>
  );
};

export const RangeSectionWithLabels = memo(({ axisName = 'yAxis' }: { axisName: AxisPropertyName }) => {
  const { t } = useTranslation('chart_widget', { useSuspense: false });
  const { y2Axis } = useWidget<ChartWidgetData>();

  const shouldRenameYAxis = y2Axis != null && axisName === 'yAxis';

  const {
    label,
    minPropertyName,
    minAriaLabel,
    maxPropertyName,
    maxAriaLabel,
  }: {
    label: string;
    minPropertyName: MinValuePropertyName;
    minAriaLabel?: string;
    maxPropertyName: MaxValuePropertyName;
    maxAriaLabel?: string;
  } = useMemo(() => {
    if (axisName === 'xAxis') {
      return {
        label: t('axis.range.xAxisRange'),
        minPropertyName: 'minXValue',
        maxPropertyName: 'maxXValue',
      };
    }

    if (axisName === 'yAxis' && shouldRenameYAxis) {
      return {
        label: t('axis.range.y1AxisRange'),
        minAriaLabel: t('axis.range.minY1ValueLabel'),
        minPropertyName: 'minValue',
        maxAriaLabel: t('axis.range.maxY1ValueLabel'),
        maxPropertyName: 'maxValue',
      };
    }

    if (axisName === 'y2Axis') {
      return {
        label: t('axis.range.y2AxisRange'),
        minPropertyName: 'minY2Value',
        maxPropertyName: 'maxY2Value',
      };
    }

    return {
      label: t('axis.range.yAxisRange'),
      minPropertyName: 'minValue',
      maxPropertyName: 'maxValue',
    };
  }, [axisName, t, shouldRenameYAxis]);

  return (
    <ChartSettingsEntry
      label={label}
      containerProps={{
        flexDirection: 'column',
        alignItems: 'flex-start',
      }}
    >
      <Stack direction={'row'} spacing={4}>
        <AxisRangeValue propertyName={minPropertyName} aria-label={minAriaLabel} />
        <AxisRangeValue propertyName={maxPropertyName} aria-label={maxAriaLabel} />
      </Stack>
    </ChartSettingsEntry>
  );
});
RangeSectionWithLabels.displayName = 'MinMaxSectionWithLabels';

export const DualMinMaxSection = () => {
  return (
    <>
      <RangeSectionWithLabels axisName='xAxis' />
      <SideSettingDivider />
      <RangeSectionWithLabels axisName='yAxis' />
    </>
  );
};

export const AxisLine = ({ widgetType, axisName, defaultValue = true }: Readonly<AxisCommonProps>) => {
  const { xAxis, yAxis, y2Axis, updateWidget } = useWidget<ChartWidgetData>();
  const { t } = useTranslation('chart_widget', { useSuspense: false });

  const axisPropertyName = getAxisPropertyName(axisName, widgetType);
  const isXYSwapped = axisPropertyName !== axisName;

  const { currentValue = defaultValue, label } = (() => {
    if (axisName === 'xAxis') {
      return {
        currentValue: isXYSwapped ? yAxis.enableLine : xAxis.enableLine,
        label: t('axis.enableLine.showXAxisLine'),
      };
    }

    if (axisName === 'yAxis') {
      const hasY2Axis = y2Axis != null;
      return {
        currentValue: isXYSwapped ? xAxis.enableLine : yAxis.enableLine,
        label: t(hasY2Axis ? 'axis.enableLine.showY1AxisLine' : 'axis.enableLine.showYAxisLine'),
      };
    }

    // y2Axis
    return {
      currentValue: y2Axis?.enableLine,
      label: t('axis.enableLine.showY2AxisLine'),
    };
  })();

  const onShowAxisLineChange = useCallback(() => {
    updateWidget({
      [axisPropertyName]: {
        enableLine: !currentValue,
      },
    });
  }, [axisPropertyName, currentValue, updateWidget]);

  return (
    <ChartSettingsEntry
      label={label}
      containerProps={{
        as: FormControl,
      }}
      labelProps={{
        as: FormLabel,
      }}
    >
      <Switch onChange={onShowAxisLineChange} isChecked={currentValue} />
    </ChartSettingsEntry>
  );
};

export const AxisLineSubSettings = () => {
  const {
    xAxis: { enableLine: enableXAxisLine = true },
    yAxis: { enableLine: enableYAxisLine = true },
    y2Axis,
  } = useWidget<ChartWidgetData>();

  const isY2AxisLineDisabled = y2Axis != null && y2Axis.enableLine === false;
  if (!enableXAxisLine && !enableYAxisLine && isY2AxisLineDisabled) {
    return null;
  }

  return (
    <>
      <AxisLineColor />
      <AxisLineWidth />
    </>
  );
};

export const AxisLineColor = () => {
  const {
    xAxis: {
      style: { lineColor },
    },
    updateWidget,
  } = useWidget<ChartWidgetData>();

  const { setAllowScrolling } = useSideMenuSetting();

  const { t } = useTranslation('chart_widget', { useSuspense: false, keyPrefix: 'axis' });
  const label = t('color.label');

  const onAxisLineColorChange = (lineColor: Color) => {
    updateWidget({
      xAxis: { style: { lineColor } },
      yAxis: { style: { lineColor } },
    });
  };

  return (
    <ChartSettingsEntry label={label}>
      <ColorPicker
        color={lineColor}
        onChange={onAxisLineColorChange}
        onOpen={setAllowScrolling.off}
        onClose={setAllowScrolling.on}
        placement='left'
      />
    </ChartSettingsEntry>
  );
};

export const AxisLineWidth = () => {
  const {
    xAxis: {
      style: { lineWidth },
    },
    updateWidget,
  } = useWidget<ChartWidgetData>();

  const { t } = useTranslation('chart_widget', { useSuspense: false, keyPrefix: 'axis' });
  const label = t('width.label');

  const onAxisLineWidthChange = (lineWidth: number) => {
    updateWidget({
      xAxis: { style: { lineWidth } },
      yAxis: { style: { lineWidth } },
    });
  };

  return (
    <SliderNumberInput
      title={label}
      value={lineWidth}
      maxValue={5}
      minValue={1}
      onChange={onAxisLineWidthChange}
      fontSize={'sm'}
      sliderWidth={'8rem'}
      containerProps={{
        sx: {
          '& > div': {
            margin: 0,
          },
          '& .chakra-slider': {
            marginRight: '0.5rem',
          },
        },
      }}
      titleProps={{ fontWeight: 'normal' }}
      numberInputProps={{ textAlign: 'left', w: 12 }}
    />
  );
};

export const ShowAxisLabel = () => {
  const {
    xAxis: { enablelabels: enableAxisLabels = true },
    updateWidget,
  } = useWidget<ChartWidgetData>();

  const { t } = useTranslation('chart_widget', { useSuspense: false, keyPrefix: 'axis' });
  const label = t('showLabel.showAxisLabels');

  const onShowAxislabelsChange = () => {
    updateWidget({
      xAxis: { enablelabels: !enableAxisLabels },
      yAxis: { enablelabels: !enableAxisLabels },
    });
  };

  return (
    <ChartSettingsEntry label={label}>
      <Switch onChange={onShowAxislabelsChange} isChecked={enableAxisLabels} />
    </ChartSettingsEntry>
  );
};

export const AxisLabelFont = () => {
  const { t } = useTranslation('chart_widget', { useSuspense: false, keyPrefix: 'font' });
  const label = t('label');

  return (
    <ChartSettingsEntry label={label}>
      <Stack direction={'row'}>
        <AxisLabelFontFamily />
        <AxisLabelFontSize />
      </Stack>
    </ChartSettingsEntry>
  );
};

export const AxisLabelFontFamily = () => {
  const {
    xAxis: {
      labelStyle: { fontFamily: axisLabelFontFamily = WIDGET_DEFAULT_FONT },
    },
    updateWidget,
  } = useWidget<ChartWidgetData>();

  const onAxislabelsFontFamilyChange = (fontFamily: string) => {
    updateWidget({
      xAxis: { labelStyle: { fontFamily } },
      yAxis: { labelStyle: { fontFamily } },
    });
  };

  return <FontFamilySelect fontFamily={axisLabelFontFamily} onChange={onAxislabelsFontFamilyChange} />;
};

export const AxisLabelFontSize = () => {
  const {
    xAxis: {
      labelStyle: { fontSize: axisLabelFontSize = 12 },
    },
    updateWidget,
  } = useWidget<ChartWidgetData>();

  const { t } = useTranslation('chart_widget', { useSuspense: false, keyPrefix: 'axis' });
  const ariaLabel = t('fontSizeSelect.ariaLabel');

  const onAxislabelsFontSizeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    updateWidget({
      xAxis: { labelStyle: { fontSize: event.target.value } },
      yAxis: { labelStyle: { fontSize: event.target.value } },
    });
  };

  return (
    <DropdownFontSizeSelect
      data-testid={'axeslabel-fontsize-select'}
      aria-label={ariaLabel}
      value={axisLabelFontSize}
      onChange={onAxislabelsFontSizeChange}
    />
  );
};

export const AxisLabelTextStyle = () => {
  const {
    xAxis: {
      labelStyle: { color: axisLabelColor = '' },
    },
    updateWidget,
  } = useWidget<ChartWidgetData>();

  const { setAllowScrolling } = useSideMenuSetting();

  const { t } = useTranslation('chart_widget', { useSuspense: false, keyPrefix: 'text' });
  const label = t('label');
  const colorLabel = t('colorLabel');

  const onAxislabelsColorChange = (color: Color) => {
    updateWidget({
      xAxis: { labelStyle: { color } },
      yAxis: { labelStyle: { color } },
    });
  };

  return (
    <ChartSettingsEntry label={label}>
      <Stack direction={'row'}>
        <ColorPicker
          iconStyle={'text'}
          color={axisLabelColor}
          label={colorLabel}
          onChange={onAxislabelsColorChange}
          onOpen={setAllowScrolling.off}
          onClose={setAllowScrolling.on}
          showNoColorOption={false}
        />
      </Stack>
    </ChartSettingsEntry>
  );
};
